/**
 * Download a document's markdown or PDF export to the user's machine.
 *
 * The backend `GET /api/documents/{id}/export` returns the document rendered as
 * a `text/markdown` or `application/pdf` attachment (`?format=md`/`?format=pdf`,
 * BLA-331). `apiUtils.fetchData` parses JSON, so it cannot carry a file body —
 * this module fetches the export directly, then triggers a browser download
 * from the resulting blob. The pure pieces (URL building, filename parsing) are
 * separated from the DOM glue so they are unit-testable.
 *
 * No PHI is logged here; the exported content flows into the download, never
 * into logs.
 */
import { applyDevIdentityParams } from "../apiUtils";
import config from "../config";

/** The exportable document formats offered by the Download control. */
export type DocumentExportFormat = "md" | "pdf";

const ACCEPT_HEADERS: Record<DocumentExportFormat, string> = {
  md: "text/markdown",
  pdf: "application/pdf",
};

/**
 * Build the absolute export URL for a document (carrying dev identity params).
 *
 * @param documentId - The document to export.
 * @param format - The export format (defaults to markdown).
 * @returns The absolute `…/export` URL to fetch.
 */
export function documentExportUrl(
  documentId: string,
  format: DocumentExportFormat = "md",
): string {
  const endpoint = `/api/documents/${encodeURIComponent(documentId)}/export?format=${format}`;
  return `${config.BACKEND_URL}${applyDevIdentityParams(endpoint)}`;
}

/**
 * Parse the download filename from a `Content-Disposition` header.
 *
 * @param header - The raw `Content-Disposition` header value, or null.
 * @returns The quoted/unquoted filename, or null when absent/unparseable.
 */
export function filenameFromContentDisposition(
  header: string | null,
): string | null {
  if (!header) return null;
  const match = /filename="?([^"]+)"?/.exec(header);
  return match ? match[1] : null;
}

/**
 * Trigger a browser download of a blob under the given filename (DOM glue).
 *
 * @param blob - The file contents.
 * @param filename - The download filename.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Fetch a document's export in the given format and save it to disk.
 *
 * @param documentId - The document to export.
 * @param format - The export format (defaults to markdown).
 * @throws Error when the export request fails (non-2xx response).
 */
export async function downloadDocument(
  documentId: string,
  format: DocumentExportFormat = "md",
): Promise<void> {
  const response = await fetch(documentExportUrl(documentId, format), {
    headers: { Accept: ACCEPT_HEADERS[format] },
  });
  if (!response.ok) {
    throw new Error(`Export failed with status ${response.status}`);
  }
  const blob = await response.blob();
  const filename =
    filenameFromContentDisposition(
      response.headers.get("Content-Disposition"),
    ) ?? `${documentId}.${format}`;
  saveBlob(blob, filename);
}
